import router from '@/router'
import { axios } from '@/plugins'
import { handleAsync, setSnackbar } from 'best-modules/plugins'
import { computed, nextTick } from 'vue'
import { urlMicrosoftPreview } from '@/utils/urls.js'
import { CustomDate, toPath } from 'best-modules/utils'
import { keyToLabel } from './key-to-label'
import { getBlobExt } from 'best-modules/vue-utils'

function isDate(value, type) {
  if (typeof value !== 'string') return false

  const ISODateRegex =
    /[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(\.[0-9]+)?([Zz]|([+-])([01]\d|2[0-3]):?([0-5]\d)?)?/i
  const ISODateWithoutTimeRegex = /[0-9]{4}-[0-9]{2}-[0-9]{2}/i

  if (type === 'response') {
    return (
      (ISODateRegex.test(value) && value.length === 27) ||
      (ISODateWithoutTimeRegex.test(value) && value.length === 10)
    )
  }
  if (type === 'request') {
    return /^\d{2}\.\d{2}\.\d{4}(, \d{2}:\d{2})?$/.test(value)
  }
}

function getErrorMessages(v$) {
  if (!v$ || typeof v$ !== 'object') {
    throw new Error(
      `function getErrorMessages expect v$ as Object, but got ${typeof v$}`
    )
  }

  return v$.$errors.map(() => {
    return "Обов'язкове поле"
  })
}

async function rerenderComponent(ref) {
  ref.value = false
  await nextTick()
  ref.value = true
}

function generateId(length = 13) {
  return Math.random()
    .toString(16)
    .substring(2, Math.min(length + 2, 13))
}

function getFile({ path, type = 'blob' /* blob | path */, token }) {
  return handleAsync(`getFile_${path}`, () => {
    const Authorization = token
      ? `Bearer ${token.replace('Bearer ', '')}`
      : null
    return axios
      .get(toPath(path), {
        responseType: 'blob',
        headers: { Authorization },
      })
      .then(res => {
        switch (type) {
          case 'path':
            return URL.createObjectURL(res.data)
          case 'blob':
            return res.data
        }
      })
  })
}

function isMicrosoftFile(file, type = 'path') {
  const microsoftExt = ['doc', 'docx', 'xls', 'xlsx']

  if (type === 'path') {
    return microsoftExt.includes(file.split('.').at(-1))
  }
  if (type === 'blob') {
    return getBlobExt(file)
  }
  if (type === 'ext') {
    return microsoftExt.includes(file)
  }
}

function documentPathHandler(path, options = {}) {
  if (isMicrosoftFile(path)) {
    return urlMicrosoftPreview(path)
  } else {
    const { token } = options
    return getFile({ path, type: 'blob', token }).then(blob => {
      console.log('blob', blob)
      return URL.createObjectURL(blob)
    })
  }
}

function convertFileFormatToLowerCase(path) {
  const parts = path.split('.')
  const extension = parts.pop().toLowerCase()
  return parts.join('.') + '.' + extension
}

async function openDocument(path) {
  const url = await documentPathHandler(path)
  window.open(url, '_blank')
}

function getQueryStr(queryObj, params = {}) {
  if (!queryObj) return ''
  const { ampersand = false } = params
  return Object.entries(queryObj)
    .filter(f => ![null, ''].includes(f[1]))
    .map((q, index) => {
      const key = q[0]
      let value = q[1]
      const separator = index === 0 && !ampersand ? '?' : '&'
      if (
        Array.isArray(value) &&
        value.length &&
        value.reduce((acc, v) => (acc ? isDate(v, 'request') : false), true)
      ) {
        return getQueryStr(
          {
            startDate: new CustomDate(value[0]).toString({ format: 'iso' }),
            endDate: new CustomDate(value[1]).toString({ format: 'iso' }),
          },
          { ampersand: true }
        )
      }
      if (isDate(value, 'request')) {
        value = new CustomDate(value).toString({ format: 'iso' })
      }
      if (Array.isArray(value)) {
        if (!value.length) {
          return ''
        }
        return `${separator}${value.map(v => `${key}=${v}`).join('&')}`
      }
      return `${separator}${key}=${value}`
    })
    .join('')
}

function getCurrentDate() {
  const currentDate = new Date()
  const year = currentDate.getFullYear()
  let month = currentDate.getMonth() + 1
  let day = currentDate.getDate()
  let hours = currentDate.getHours()
  let minutes = currentDate.getMinutes()

  month = month < 10 ? '0' + month : month
  day = day < 10 ? '0' + day : day
  hours = hours < 10 ? '0' + hours : hours
  minutes = minutes < 10 ? '0' + minutes : minutes

  return `${day}.${month}.${year}, ${hours}:${minutes}`
}

function navigateToItem(name, id) {
  const route = { name }
  if (id) {
    route.params = typeof id === 'object' ? id : { id }
  }
  router.push(route)
}

function getEntityId(entity, key = 'entity') {
  const e = entity?.[key] ? entity[key] : entity
  const ENTITY_TYPE_ID = `${key}TypeId`
  const ENTITY_ID = `${key}Id`

  if (e && e[ENTITY_TYPE_ID] && e[ENTITY_ID]) {
    return `${e[ENTITY_TYPE_ID]}${e[ENTITY_ID]}`
  } else if (e && e.id) {
    return e.id
  }

  console.error('Invalid entity:', e)
  return null
}

function reactiveProp(
  props,
  emit = () => false,
  propName = 'modelValue',
  defaultValue = null,
  formatter = null,
  setFunc = null
) {
  return computed({
    get: () => props[propName] || defaultValue,
    set: val => {
      const res = formatter ? formatter(val) : val
      setFunc ? setFunc(res) : emit(`update:${propName}`, res)
    },
  })
}

function hideTime(date) {
  if (!date) return date
  return date.substring(0, 10)
}

function keepArray(value) {
  return Array.isArray(value) ? value : [value].filter(Boolean)
}

function filterDuplicates(arr, key = 'id') {
  return arr.filter((itm, idx, arr) => {
    return itm && arr.findIndex(i => i[key] === itm[key]) === idx
  })
}

function getCurrentYearDeclension(numberDate) {
  const age = parseInt(numberDate)

  if (isNaN(age)) {
    return 'Невірний формат числа'
  }

  let years
  if (age % 10 === 1 && age % 100 !== 11) {
    years = 'рік'
  } else if (
    age % 10 >= 2 &&
    age % 10 <= 4 &&
    (age % 100 < 10 || age % 100 >= 20)
  ) {
    years = 'роки'
  } else {
    years = 'років'
  }

  return `${age} ${years}`
}

function getAddressStr(address) {
  if (!address) return 'Адреса відсутня'

  const joinParties = (...args) =>
    args.filter(v => v !== null && v !== undefined && v !== '').join(', ')

  const formatAddress = addr => {
    if (!addr || typeof addr !== 'object') return 'Адреса вiдсутня'

    const country = addr.country?.name
    const region = addr.region
    const district = addr.area || addr.district?.district
    const city = addr.cityType ? `${addr.cityType} ${addr.city}` : addr.city
    const street =
      addr.streetType && addr.street
        ? `${addr.streetType} ${addr.street}`
        : addr.street
    const house = [addr.house, addr.houseNumAdd].filter(v => v).join(' ')
    const index = addr.index || addr.house?.Index_

    return joinParties(country, index, region, district, city, street, house)
  }

  if (Array.isArray(address)) {
    if (!address.length) return 'Адреса вiдсутня'
    const registeredAddress = address.find(a => a.type?.id === 4)
    return formatAddress(registeredAddress || address[0])
  } else if (typeof address === 'object' && Object.keys(address).length) {
    return formatAddress(address)
  } else if (typeof address === 'string') {
    return address
  }

  return 'Адреса вiдсутня'
}

function playSound(url) {
  const myAudioElement = new Audio(url)
  myAudioElement.addEventListener('canplaythrough', () => {
    try {
      return myAudioElement.play()
    } catch (e) {
      console.warn(e)
    }
  })
}

const formatFileSize = bytes => {
  if (bytes === 0) {
    return '0 B'
  }

  const k = 1000,
    dm = 2,
    sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    i = Math.floor(Math.log(bytes) / Math.log(k))

  return Number((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

const notifyValidation = (v$, scope, overrides) => {
  if (v$.$errors.length) {
    setSnackbar({
      title: 'Помилка валідації',
      color: 'error',
      text: [
        {
          tag: 'ul',
          children: v$.$errors
            // remove repeated $property
            .filter((err, idx, arr) => {
              return arr.findIndex(v => v.$property === err.$property) === idx
            })
            .map(e => {
              return {
                tag: 'li',
                text: keyToLabel(e.$property, scope, overrides),
              }
            }),
        },
      ],
    })
  }
}

/**
 * @param {Date|string} startDate - Start date (Date object or parsable date string)
 * @param {Date|string} endDate - End date (Date object or parsable date string)
 * @returns {Date[]} Array of Date objects for each day in the range
 */
const getDatesInRange = (startDate, endDate) => {
  if (!startDate || !endDate) return []

  const dates = []

  const start = new Date(startDate).setHours(0, 0, 0, 0)
  const end = new Date(endDate).setHours(0, 0, 0, 0)

  const current = new Date(start)

  while (current <= end) {
    dates.push(new Date(current))
    current.setDate(current.getDate() + 1)
  }

  return dates
}

export {
  rerenderComponent,
  generateId,
  getFile,
  documentPathHandler,
  formatFileSize,
  openDocument,
  getCurrentDate,
  isMicrosoftFile,
  getQueryStr,
  navigateToItem,
  getEntityId,
  reactiveProp,
  hideTime,
  keepArray,
  filterDuplicates,
  getAddressStr,
  getErrorMessages,
  getCurrentYearDeclension,
  playSound,
  convertFileFormatToLowerCase,
  notifyValidation,
  getDatesInRange,
}
